import { 
    Container, 
    Grid, 
    Paper, 
    Typography, 
    Box, 
    Card, 
    CardContent,
    Chip,
    Button
} from "@mui/material";
import { Link } from "react-router-dom";
import SeeNotice from "../../components/SeeNotice";
import styled, { keyframes } from "styled-components";
import CountUp from "react-countup";
import { useDispatch, useSelector } from "react-redux";
import { useEffect, useState } from "react";
import { getAllSclasses } from "../../redux/sclassRelated/sclassHandle";
import { getAllStudents } from "../../redux/studentRelated/studentHandle";
import { getAllTeachers } from "../../redux/teacherRelated/teacherHandle";
import { getSalarySummary } from "../../redux/salaryRelated/salaryHandle";
import { getAllStaffs, getAllSimpleStaffs } from "../../redux/staffRelated/staffHandle";
import { getAllNotices } from "../../redux/noticeRelated/noticeHandle";
import { getAllParents } from "../../redux/parentRelated/parentHandle";
import { getAllComplains } from "../../redux/complainRelated/complainHandle";
import { clearStaffList } from "../../redux/staffRelated/staffSlice";
import { formatDatePart, getMonthNameFromDate } from "../../utils/customDate";

// Icons
import PeopleIcon from "@mui/icons-material/People";
import ClassIcon from "@mui/icons-material/Class";
import SchoolIcon from "@mui/icons-material/School";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import NotificationsIcon from "@mui/icons-material/Notifications";
import GroupIcon from "@mui/icons-material/Group";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import CampaignIcon from "@mui/icons-material/Campaign";
import ViewListIcon from "@mui/icons-material/ViewList";
import AssessmentIcon from "@mui/icons-material/Assessment";
import PaymentIcon from "@mui/icons-material/Payment";
import FamilyRestroomIcon from "@mui/icons-material/FamilyRestroom";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";

// Animation keyframes
const fadeIn = keyframes`
    from { opacity: 0; transform: translateY(20px); }
    to { opacity: 1; transform: translateY(0); }
`;

const float = keyframes`
    0% { transform: translateY(0px); }
    50% { transform: translateY(-5px); }
    100% { transform: translateY(0px); }
`;

const pulse = keyframes`
    0% { box-shadow: 0 0 0 0 rgba(100, 181, 246, 0.4); }
    70% { box-shadow: 0 0 0 10px rgba(100, 181, 246, 0); }
    100% { box-shadow: 0 0 0 0 rgba(100, 181, 246, 0); }
`;

const shimmer = keyframes`
    0% { background-position: -1000px 0; }
    100% { background-position: 1000px 0; }
`;

const AdminHomePage = () => {
    const dispatch = useDispatch();
    const { studentsList } = useSelector((state) => state.student);
    const { sclassesList } = useSelector((state) => state.sclass);
    const { teachersList } = useSelector((state) => state.teacher);
    const { salaryRecords } = useSelector((state) => state.salary);
    const { noticesList = [] } = useSelector((state) => state.notice);
    const { staffList } = useSelector((state) => state.staff);
    const { parentList = [] } = useSelector((state) => state.parent || {});
    const { complainsList = [] } = useSelector((state) => state.complain || {});
    const { currentUser } = useSelector((state) => state.user);

    const adminID = currentUser?._id;
    const adminName = currentUser?.name || "Admin";

    const [currentDate, setCurrentDate] = useState(new Date());

    useEffect(() => {
        if (adminID) {
            dispatch(clearStaffList());
            dispatch(getAllStudents(adminID));
            dispatch(getAllSclasses(adminID, "Sclass"));
            dispatch(getAllTeachers(adminID));
            dispatch(getSalarySummary(adminID));
            dispatch(getAllStaffs(adminID));
            dispatch(getAllSimpleStaffs(adminID));
            dispatch(getAllNotices(adminID, 'Notice'));
            dispatch(getAllParents(adminID));
            dispatch(getAllComplains(adminID, "Complain"));
        }
    }, [adminID, dispatch]);

    // Update date every minute
    useEffect(() => {
        const timer = setInterval(() => setCurrentDate(new Date()), 60000);
        return () => clearInterval(timer);
    }, []);

    const numberOfStudents = studentsList && studentsList.length;
    const numberOfClasses = sclassesList && sclassesList.length;
    const numberOfTeachers = teachersList && teachersList.length;
    const numberOfStaff = (staffList?.length || 0);
    const numberOfNotices = noticesList.length;
    const numberOfParents = parentList ? parentList.length : 0;
    const numberOfComplains = complainsList ? complainsList.length : 0;

    // Calculate total salary records from salaryRecords
    const salaryData = salaryRecords || {};
    const totalSalaryRecords = 
        (salaryData.byEmployeeType?.teacher?.count || 0) +
        (salaryData.byEmployeeType?.staff?.count || 0) +
        (salaryData.byEmployeeType?.admin?.count || 0);

    const formatDate = (date) => {
        const options = { 
            weekday: 'long', 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric' 
        };
        return date.toLocaleDateString('en-US', options);
    };

    return (
        <PageContainer>
            <Container maxWidth="xl">
                {/* Welcome Header Section */}
                <WelcomeSection>
                    <WelcomeInfo>
                        <WelcomeTitle>Welcome back, {adminName}! 👋</WelcomeTitle>
                        <WelcomeSubtitle>Here's what's happening in your school today</WelcomeSubtitle>
                    </WelcomeInfo>
                </WelcomeSection>

                {/* Stats Cards Section */}
                <SectionTitle>📊 Quick Statistics</SectionTitle>
                <StatsGrid container spacing={3}>
                    {/* Total Students Card */}
                    <Grid item xs={12} sm={6} md={4} lg={3}>
                        <StatCard $animationDelay="0.1s">
                            <StatIconWrapper $color="#64b5f6">
                                <PeopleIcon />
                            </StatIconWrapper>
                            <StatCount>
                                <Data start={0} end={numberOfStudents} duration={2.5} />
                            </StatCount>
                            <StatLabel>Total Students</StatLabel>
                        </StatCard>
                    </Grid>

                    {/* Total Classes Card */}
                    <Grid item xs={12} sm={6} md={4} lg={3}>
                        <StatCard $animationDelay="0.2s">
                            <StatIconWrapper $color="#ffb74d">
                                <ClassIcon />
                            </StatIconWrapper>
                            <StatCount>
                                <Data start={0} end={numberOfClasses} duration={2.5} />
                            </StatCount>
                            <StatLabel>Total Classes</StatLabel>
                        </StatCard>
                    </Grid>

                    {/* Total Teachers Card */}
                    <Grid item xs={12} sm={6} md={4} lg={3}>
                        <StatCard $animationDelay="0.3s">
                            <StatIconWrapper $color="#81c784">
                                <SchoolIcon />
                            </StatIconWrapper>
                            <StatCount>
                                <Data start={0} end={numberOfTeachers} duration={2.5} />
                            </StatCount>
                            <StatLabel>Total Teachers</StatLabel>
                        </StatCard>
                    </Grid>

                    {/* Total Staff Card */}
                    <Grid item xs={12} sm={6} md={4} lg={3}>
                        <StatCard $animationDelay="0.4s">
                            <StatIconWrapper $color="#ba68c8">
                                <GroupIcon />
                            </StatIconWrapper>
                            <StatCount>
                                <Data start={0} end={numberOfStaff} duration={2.5} />
                            </StatCount>
                            <StatLabel>Total Staff</StatLabel>
                        </StatCard>
                    </Grid>

                    {/* Total Parents Card */}
                    <Grid item xs={12} sm={6} md={4} lg={3}>
                        <StatCard $animationDelay="0.5s">
                            <StatIconWrapper $color="#f06292">
                                <FamilyRestroomIcon />
                            </StatIconWrapper>
                            <StatCount>
                                <Data start={0} end={numberOfParents} duration={2.5} />
                            </StatCount>
                            <StatLabel>Total Parents</StatLabel>
                        </StatCard>
                    </Grid>

                    {/* Salary Records Card */}
                    <Grid item xs={12} sm={6} md={4} lg={3}>
                        <StatCard $animationDelay="0.6s">
                            <StatIconWrapper $color="#ff8a65">
                                <AttachMoneyIcon />
                            </StatIconWrapper>
                            <StatCount>
                                <Data start={0} end={totalSalaryRecords} duration={2} />
                            </StatCount>
                            <StatLabel>Salary Records</StatLabel>
                        </StatCard>
                    </Grid>

                    {/* Notices Card */}
                    <Grid item xs={12} sm={6} md={4} lg={3}>
                        <StatCard $animationDelay="0.7s">
                            <StatIconWrapper $color="#4dd0e1">
                                <NotificationsIcon />
                            </StatIconWrapper>
                            <StatCount>
                                <Data start={0} end={numberOfNotices} duration={1.5} />
                            </StatCount>
                            <StatLabel>Active Notices</StatLabel>
                        </StatCard>
                    </Grid>

                    {/* Complains Card */}
                    <Grid item xs={12} sm={6} md={4} lg={3}>
                        <StatCard $animationDelay="0.8s">
                            <StatIconWrapper $color="#e57373">
                                <ReportProblemIcon />
                            </StatIconWrapper>
                            <StatCount>
                                <Data start={0} end={numberOfComplains} duration={2.5} />
                            </StatCount>
                            <StatLabel>Total Complains</StatLabel>
                        </StatCard>
                    </Grid>
                </StatsGrid>

                {/* Quick Actions Section */}
                <SectionTitle>⚡ Quick Actions</SectionTitle>
                <ActionsGrid container spacing={2}>
                    <Grid item xs={6} sm={4} md={2}>
                        <ActionCard to="/Admin/addstudents">
                            <ActionIcon $color="#64b5f6">
                                <PersonAddIcon fontSize="inherit" />
                            </ActionIcon>
                            <ActionLabel>Add Student</ActionLabel>
                        </ActionCard>
                    </Grid>
                    <Grid item xs={6} sm={4} md={2}>
                        <ActionCard to="/Admin/teachers/addteacher">
                            <ActionIcon $color="#81c784">
                                <PersonAddAlt1Icon fontSize="inherit" />
                            </ActionIcon>
                            <ActionLabel>Add Teacher</ActionLabel>
                        </ActionCard>
                    </Grid>
                    <Grid item xs={6} sm={4} md={2}>
                        <ActionCard to="/Admin/addnotice">
                            <ActionIcon $color="#ffb74d">
                                <CampaignIcon fontSize="inherit" />
                            </ActionIcon>
                            <ActionLabel>Add Notice</ActionLabel>
                        </ActionCard>
                    </Grid>
                    <Grid item xs={6} sm={4} md={2}>
                        <ActionCard to="/Admin/classes">
                            <ActionIcon $color="#ba68c8">
                                <ViewListIcon fontSize="inherit" />
                            </ActionIcon>
                            <ActionLabel>View Classes</ActionLabel>
                        </ActionCard>
                    </Grid>
                    <Grid item xs={6} sm={4} md={2}>
                        <ActionCard to="/Admin/results">
                            <ActionIcon $color="#ff8a65">
                                <AssessmentIcon fontSize="inherit" />
                            </ActionIcon>
                            <ActionLabel>View Results</ActionLabel>
                        </ActionCard>
                    </Grid>
                    <Grid item xs={6} sm={4} md={2}>
                        <ActionCard to="/Admin/fees">
                            <ActionIcon $color="#4dd0e1">
                                <PaymentIcon fontSize="inherit" />
                            </ActionIcon>
                            <ActionLabel>View Fees</ActionLabel>
                        </ActionCard>
                    </Grid>
                </ActionsGrid>

                {/* Notice & Complains Section */}
                <Grid container spacing={4} sx={{ mb: 4 }}>
                    {/* Notices Column */}
                    <Grid item xs={12} md={8}>
                        <SectionTitle>📢 School Announcements</SectionTitle>
                        <NoticePaper>
                            <NoticeHeader>
                                <Box>
                                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#1a1a2e' }}>Latest Notices</Typography>
                                    <Typography variant="body2" sx={{ color: '#666', mt: 0.5 }}>Stay updated with recent events</Typography>
                                </Box>
                                <Button variant="outlined" component={Link} to="/Admin/notices" sx={{ borderRadius: '20px', textTransform: 'none', fontWeight: 600 }}>View All</Button>
                            </NoticeHeader>
                            <Box sx={{ p: 0 }}>
                                {noticesList && noticesList.length > 0 ? (
                                    noticesList.slice(0, 5).map((notice, index) => {
                                        const dateStr = formatDatePart(notice.date);
                                        const monthName = getMonthNameFromDate(dateStr) || '---';
                                        const day = dateStr && dateStr.includes('-') ? dateStr.split('-')[2] : '--';
                                        return (
                                        <NoticeListItem key={notice._id || index} $isLast={index === Math.min(noticesList.length, 5) - 1}>
                                            <NoticeDateBlock>
                                                <Typography variant="h5" sx={{ fontWeight: 800, color: '#667eea', lineHeight: 1 }}>
                                                    {day}
                                                </Typography>
                                                <Typography variant="caption" sx={{ color: '#888', textTransform: 'uppercase', fontWeight: 700, letterSpacing: '1px' }}>
                                                    {monthName}
                                                </Typography>
                                            </NoticeDateBlock>
                                            <NoticeContent>
                                                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1a1a2e', mb: 0.5 }}>
                                                    {notice.title}
                                                </Typography>
                                                <Typography variant="body2" sx={{ color: '#666', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                                                    {notice.details}
                                                </Typography>
                                            </NoticeContent>
                                        </NoticeListItem>
                                    )})
                                ) : (
                                    <Box sx={{ p: 4, textAlign: 'center' }}>
                                        <Typography variant="body1" sx={{ color: '#888' }}>No new notices available.</Typography>
                                    </Box>
                                )}
                            </Box>
                        </NoticePaper>
                    </Grid>

                    {/* Complains Column */}
                    <Grid item xs={12} md={4}>
                        <SectionTitle>⚠️ Recent Feedback</SectionTitle>
                        <NoticePaper>
                            <NoticeHeader sx={{ background: '#fffafb', borderBottom: '1px solid #ffebee' }}>
                                <Box>
                                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#d32f2f' }}>User Complains</Typography>
                                    <Typography variant="body2" sx={{ color: '#e57373', mt: 0.5 }}>Attention required</Typography>
                                </Box>
                                <Button variant="outlined" color="error" component={Link} to="/Admin/complains" sx={{ borderRadius: '20px', textTransform: 'none', fontWeight: 600 }}>Resolve</Button>
                            </NoticeHeader>
                            <Box sx={{ p: 0 }}>
                                {complainsList && complainsList.length > 0 ? (
                                    complainsList.slice(0, 5).map((complain, index) => {
                                        const dateStr = formatDatePart(complain.date);
                                        const monthName = getMonthNameFromDate(dateStr) || '---';
                                        const day = dateStr && dateStr.includes('-') ? dateStr.split('-')[2] : '--';
                                        return (
                                        <ComplainListItem key={complain._id || index} $isLast={index === Math.min(complainsList.length, 5) - 1}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1 }}>
                                                <Chip label={`${monthName} ${day}`} size="small" sx={{ bgcolor: '#ffebee', color: '#d32f2f', fontWeight: 600, fontSize: '0.7rem' }} />
                                                <Typography variant="caption" sx={{ color: '#888', fontWeight: 600 }}>From: {complain.user?.name || 'User'}</Typography>
                                            </Box>
                                            <Typography variant="body2" sx={{ color: '#1a1a2e', fontWeight: 500, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                                                "{complain.complaint}"
                                            </Typography>
                                        </ComplainListItem>
                                    )})
                                ) : (
                                    <Box sx={{ p: 4, textAlign: 'center' }}>
                                        <Typography variant="body1" sx={{ color: '#888' }}>No recent complains. Great job!</Typography>
                                    </Box>
                                )}
                            </Box>
                        </NoticePaper>
                    </Grid>
                </Grid>

                {/* Footer */}
                <DashboardFooter>
                    <FooterText>🎓 Student Management System • Admin Dashboard </FooterText>
                    <FooterText>All rights reserved © 2026 -By Arphatech Solution</FooterText>
                </DashboardFooter>
            </Container>
        </PageContainer>
    );
};

// Styled Components
const PageContainer = styled.div`
    min-height: 100vh;
    background: #f4f6f8;
    
    @media (max-width: 600px) {
        padding: 8px;
    }
`;

const WelcomeSection = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 36px 40px;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    border-radius: 24px;
    margin-bottom: 40px;
    box-shadow: 0 10px 30px rgba(118, 75, 162, 0.3);
    position: relative;
    overflow: hidden;
    
    &::before {
        content: '';
        position: absolute;
        top: -20px;
        left: -20px;
        width: 150px;
        height: 150px;
        background: radial-gradient(circle, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 70%);
        border-radius: 50%;
    }

    &::after {
        content: '';
        position: absolute;
        bottom: -50px;
        right: -30px;
        width: 250px;
        height: 250px;
        background: radial-gradient(circle, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 70%);
        border-radius: 50%;
    }
    
    @media (max-width: 600px) {
        flex-direction: column;
        text-align: center;
        gap: 16px;
        padding: 24px;
    }
`;

const WelcomeInfo = styled.div`
    animation: ${fadeIn} 0.6s ease-out;
    z-index: 1;
`;

const WelcomeTitle = styled.h1`
    font-size: 2rem;
    font-weight: 800;
    color: #ffffff;
    margin: 0 0 10px 0;
    letter-spacing: -0.5px;
    
    @media (max-width: 600px) {
        font-size: 1.5rem;
    }
`;

const WelcomeSubtitle = styled.p`
    font-size: 1rem;
    color: rgba(255, 255, 255, 0.9);
    margin: 0;
    font-weight: 400;
`;

const DateBadge = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 20px;
    background: rgba(255, 255, 255, 0.15);
    border-radius: 12px;
    color: #ffffff;
    font-weight: 500;
    font-size: 0.9rem;
    backdrop-filter: blur(10px);
    animation: ${fadeIn} 0.6s ease-out 0.2s both;
    z-index: 1;
    border: 1px solid rgba(255, 255, 255, 0.2);
    
    &:hover {
        background: rgba(255, 255, 255, 0.25);
    }
`;

const DateIcon = styled.span`
    font-size: 1.1rem;
`;

const SectionTitle = styled.h2`
    font-size: 1.4rem;
    font-weight: 700;
    color: #1a1a2e;
    margin: 0 0 24px 0;
    padding-left: 8px;
    display: flex;
    align-items: center;
    gap: 10px;
`;

const StatsGrid = styled(Grid)`
    margin-bottom: 48px;
`;

const StatIconWrapper = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 60px;
    height: 60px;
    border-radius: 16px;
    background: ${({ $color }) => `${$color}15`};
    color: ${({ $color }) => $color};
    margin-bottom: 16px;
    transition: all 0.3s ease;
    
    & svg {
        font-size: 32px;
    }
`;

const StatCard = styled(Card)`
    border-radius: 24px !important;
    background: #ffffff !important;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.03) !important;
    transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275) !important;
    animation: ${fadeIn} 0.6s ease-out ${({ $animationDelay }) => $animationDelay || '0s'} both;
    height: 180px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    border: 1px solid rgba(0,0,0,0.02);
    
    &:hover {
        transform: translateY(-10px);
        box-shadow: 0 20px 40px rgba(0, 0, 0, 0.08) !important;
        border-color: rgba(0,0,0,0.05);

        ${StatIconWrapper} {
            animation: ${float} 2s ease-in-out infinite;
            background: ${({ $color }) => `${$color}25`};
        }
    }
`;

const StatCount = styled.div`
    margin-bottom: 4px;
`;

const StatLabel = styled.p`
    font-size: 0.95rem;
    color: #888;
    margin: 0;
    font-weight: 600;
`;

const Data = styled(CountUp)`
    font-size: calc(2rem + 0.5vw);
    font-weight: 800;
    color: #1a1a2e;
    letter-spacing: -1px;
`;

const ActionsGrid = styled(Grid)`
    margin-bottom: 48px;
`;

const ActionCard = styled(Link)`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 24px 16px;
    background: #ffffff;
    border-radius: 20px;
    text-decoration: none;
    color: inherit;
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.03);
    transition: all 0.3s ease;
    border: 2px solid transparent;
    height: 100%;
    
    &:hover {
        transform: translateY(-5px);
        box-shadow: 0 15px 35px rgba(0, 0, 0, 0.08);
        border-color: ${({ $color }) => `${$color}50`};
    }
`;

const ActionIcon = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 56px;
    height: 56px;
    border-radius: 16px;
    background: ${({ $color }) => `${$color}15`};
    color: ${({ $color }) => $color};
    margin-bottom: 12px;
    font-size: 28px;
    transition: all 0.3s ease;
    
    ${ActionCard}:hover & {
        background: ${({ $color }) => $color};
        color: #ffffff;
        box-shadow: 0 8px 20px ${({ $color }) => `${$color}40`};
        transform: scale(1.05);
    }
`;

const ActionLabel = styled.span`
    font-size: 0.95rem;
    font-weight: 700;
    color: #1a1a2e;
    text-align: center;
`;

const NoticePaper = styled(Paper)`
    border-radius: 24px !important;
    overflow: hidden;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.04) !important;
    border: 1px solid rgba(0,0,0,0.02);
    background: #ffffff;
`;

const NoticeHeader = styled.div`
    padding: 24px 32px;
    background: #f8f9fa;
    border-bottom: 1px solid #eee;
    display: flex;
    align-items: center;
    justify-content: space-between;
    
    @media (max-width: 600px) {
        flex-direction: column;
        align-items: flex-start;
        gap: 16px;
    }
`;

const NoticeListItem = styled.div`
    display: flex;
    padding: 20px 32px;
    border-bottom: ${({ $isLast }) => ($isLast ? 'none' : '1px solid #f0f0f0')};
    transition: background-color 0.2s ease;
    
    &:hover {
        background-color: #f8f9fa;
    }
    
    @media (max-width: 600px) {
        padding: 20px;
    }
`;

const NoticeDateBlock = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-width: 65px;
    height: 65px;
    background: #f0f4ff;
    border-radius: 16px;
    margin-right: 20px;
    border: 1px solid rgba(102, 126, 234, 0.1);
`;

const NoticeContent = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: center;
`;

const ComplainListItem = styled.div`
    padding: 16px 32px;
    border-bottom: ${({ $isLast }) => ($isLast ? 'none' : '1px solid #f0f0f0')};
    transition: background-color 0.2s ease;
    
    &:hover {
        background-color: #fffafb;
    }
    
    @media (max-width: 600px) {
        padding: 16px 20px;
    }
`;

const DashboardFooter = styled.div`
    margin-top: 40px;
    padding: 32px 0;
    text-align: center;
    border-top: 1px solid #e0e0e0;
`;

const FooterText = styled.p`
    font-size: 0.9rem;
    color: #888;
    margin: 0;
    font-weight: 500;
`;

export default AdminHomePage;

